//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useState } from 'react';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { FilledInput, FormControl, Grow, IconButton, Tooltip } from '@mui/material';
import { newFolder, refreshFiles } from '../../../redux/slices/fileManagerSlice';
import ClearSearchButton from '../../_common/IconButtons/ClearSearchButton';
import { selectCanWriteFileManager } from '../../../redux/selectors/authSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const NewFolder = () => {
  const dispatch = useAppDispatch();
  const canWriteFileManager = useAppSelector(selectCanWriteFileManager);
  const [displayTextbox, setDisplayTextbox] = useState(false);
  const [displayFolderIcon, setDisplayFolderIcon] = useState(true);
  const [newFolderName, setNewFolderName] = useState('');

  if (!canWriteFileManager) return null;

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSubmitNewFolder();
    }
  };

  const onSubmitNewFolder = async () => {
    if (newFolderName.trim().length) {
      await dispatch(newFolder(newFolderName));
      dispatch(refreshFiles());
      setDisplayTextbox(false);
      showDelayedFolderIcon();
    }
  };

  const onCancel = () => {
    setNewFolderName('');
    setDisplayTextbox(false);
    showDelayedFolderIcon();
  };

  const handleBlur = () => {
    if (!newFolderName.length) {
      setDisplayTextbox(false);
      showDelayedFolderIcon();
    }
  };

  const showDelayedFolderIcon = () => {
    setTimeout(() => {
      setDisplayFolderIcon(true);
      setNewFolderName('');
    }, 400);
  };

  const endAdornment = newFolderName.length ? (
    <ClearSearchButton onClick={onCancel} />
  ) : (
    <NewFolderSearchIcon icon="folder_create" onMouseDown={onSubmitNewFolder} />
  );

  return displayFolderIcon ? (
    <Tooltip title="New Folder">
      <IconButton
        data-testid="new-folder-btn"
        sx={{ mr: 0.75, height: 40, width: 40 }}
        color="primary"
        onClick={() => {
          setDisplayTextbox(true);
          setDisplayFolderIcon(false);
        }}
      >
        <PolarisIcon icon="folder_create" />
      </IconButton>
    </Tooltip>
  ) : (
    <Grow in={displayTextbox} timeout={400} style={{ transformOrigin: 'center right' }}>
      <FormControl variant="standard">
        <StyledFilledInput
          onBlur={handleBlur}
          autoFocus
          id="search-bar"
          inputProps={{
            'data-testid': 'search-bar'
          }}
          value={newFolderName}
          onKeyUp={handleKeyUp}
          onChange={(event) => setNewFolderName(event.target.value)}
          endAdornment={endAdornment}
          sx={{ width: 150 }}
        />
      </FormControl>
    </Grow>
  );
};

const NewFolderSearchIcon = styled(PolarisIcon)`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.default.main};
  width: 30px;
  height: 30px;
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`;

const StyledFilledInput = styled(FilledInput)(
  ({ theme }) => `
  margin-right: ${theme.spacing(1)};
  border: 0;
  border-radius: 50px;
  transition: ease opacity 2000ms;
  & input {
    padding: ${theme.spacing(1.25, 2)};
  }
  &::placeholder {
    opacity: 0.7;
  }
  &:after, &:before {
    display: none;
    border: 0;
  }
`
);

export default NewFolder;
